/**
 * Extract channelId and streamId from a Phenix digest token (read-only, no verification).
 * Token format: DIGEST: + base64(JSON({ applicationId, digest, token: JSON(payload) })).
 * Used to detect when a token refresh targets the same stream — skip ch.token update to avoid
 * unnecessary SDK reconnection and getStats errors.
 *
 * @param {string} encodedToken - Token string (may have DIGEST: prefix)
 * @returns {{ channelId: string | null, streamId: string | null } | null} - Parsed ids, or null on parse failure
 */
export function parseTokenIds(encodedToken) {
  if (!encodedToken || typeof encodedToken !== 'string') return null;

  try {
    let payload = encodedToken;
    if (payload.startsWith('DIGEST:')) {
      payload = payload.slice('DIGEST:'.length);
    }
    const decoded = atob(payload);
    const info = JSON.parse(decoded);
    if (!info?.token || typeof info.token !== 'string') return null;

    const tokenPayload = JSON.parse(info.token);
    const channelId = tokenPayload.requiredTag?.startsWith('channelId:')
      ? tokenPayload.requiredTag.slice('channelId:'.length)
      : null;
    const streamId = tokenPayload.originStreamId ?? null;

    return { channelId, streamId };
  } catch {
    return null;
  }
}

/**
 * Compare two tokens by their channel/stream identity.
 * Returns true if both target the same stream (same channelId + streamId), so no update needed.
 *
 * @param {string} tokenA
 * @param {string} tokenB
 * @returns {boolean} true if same channel/stream
 */
export function tokenTargetsSameStream(tokenA, tokenB) {
  const a = parseTokenIds(tokenA);
  const b = parseTokenIds(tokenB);
  if (!a || !b) return false;
  return a.channelId === b.channelId && a.streamId === b.streamId;
}
